import { BrowserRouter as Router, Routes, Route, Link, Navigate } from "react-router-dom";
import logo from "./find-a-feeling-no-background.png";
import Button from "./components/shared/Button";
import LandingPage from "./components/landingPage";
import PreLaunchLandingPage from "./components/PreLaunchLandingPage";
import HomePage from "./components/homePage";
import QuizPage from "./components/quizPage";
import ResourcesPage from "./components/resourcesPage";
import AppNavigationMenu from "./components/shared/NavigationMenu";
import Footer from "./components/shared/Footer";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { GlobalLoadingOverlay } from "./components/GlobalLoadingOverlay";
import { createTheme } from "./styles/theme";
import { cn } from "./lib/utils";
import { BasePageProps } from "./types/components";
import { useModeDetection } from "./hooks/useModeDetection";
import { useAppSelector } from "./store/hooks";
import { selectIsLoading, selectAgeRange, selectIsLightMode } from "./store/slices/appSlice";

// Component to handle app pages
function AppWrapper() {
  const { currentMode } = useModeDetection();

  // Get user state from Redux
  const ageRange = useAppSelector(selectAgeRange);
  const isLightMode = useAppSelector(selectIsLightMode);

  // Get the theme based on current mode
  const theme = createTheme(isLightMode);

  // Custom button styles based on theme mode
  const buttonClasses = cn(
    "mt-10 ml-auto mr-24 px-8 rounded-3xl",
    "font-bold font-slab text-center",
    "flex items-center justify-center",
    "transition-all duration-100 transform hover:scale-110",
    "text-text-primary",
    isLightMode
      ? "bg-brand-primary hover:bg-brand-primary/80 h-10 text-lg"
      : "bg-brand-accent-coral hover:bg-brand-accent-coral/80 h-14 text-xl"
  );

  // Common props for all pages
  const pageProps: BasePageProps = {
    userMode: currentMode,
    userAge: ageRange,
  };

  return (
    <div className={`App ${theme.colors.bg.primary} flex flex-col min-h-screen`}>
      <RouteErrorBoundary>
        <AppNavigationMenu logo={logo} title="Find a Feeling" />
      </RouteErrorBoundary>

      <Link to="/quiz">
        <Button variant="primary" size="lg" className={buttonClasses}>
          Start Quiz
        </Button>
      </Link>

      <div className="container mx-auto px-4 py-8 flex-grow">
        <RouteErrorBoundary>
          <Routes>
            {/* Explorer (Kids) routes */}
            <Route path="/explorer/home" element={<HomePage {...pageProps} />} />
            <Route path="/explorer/quiz" element={<QuizPage {...pageProps} />} />
            <Route path="/explorer/resources" element={<ResourcesPage {...pageProps} />} />

            {/* Adult routes (default mode) */}
            <Route path="/home" element={<HomePage {...pageProps} />} />
            <Route path="/quiz" element={<QuizPage {...pageProps} />} />
            <Route path="/resources" element={<ResourcesPage {...pageProps} />} />

            {/* Admin/Parent routes */}
            <Route path="/admin/home" element={<HomePage {...pageProps} />} />
            <Route path="/admin/quiz" element={<QuizPage {...pageProps} />} />
            <Route path="/admin/resources" element={<ResourcesPage {...pageProps} />} />

            <Route
              path="*"
              element={
                <div className="fullscreen-center bg-surface-default text-text-primary p-4">
                  <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">🗺️</div>
                    <h1 className="text-2xl font-bold font-title mb-4">Page Not Found</h1>
                    <p className="text-lg mb-6">
                      The page you're looking for seems to have wandered off. Let's get you back on
                      track!
                    </p>
                    <div className="space-y-4">
                      <Link
                        to="/home"
                        className="inline-block bg-brand-primary hover:bg-brand-primary/80 text-text-primary px-6 py-3 rounded-lg font-medium transition-colors"
                      >
                        Go Home
                      </Link>
                      <Link
                        to="/quiz"
                        className="inline-block bg-brand-secondary hover:bg-brand-secondary/80 text-text-inverse px-6 py-3 rounded-lg font-medium transition-colors ml-4"
                      >
                        Start Quiz
                      </Link>
                    </div>
                  </div>
                </div>
              }
            />
          </Routes>
        </RouteErrorBoundary>
      </div>

      <RouteErrorBoundary>
        <Footer isLightMode={isLightMode} />
      </RouteErrorBoundary>
    </div>
  );
}

function App() {
  return (
    <Router>
      <RouteErrorBoundary>
        <AppWithLoading />
      </RouteErrorBoundary>
    </Router>
  );
}

function AppWithLoading() {
  const isLoading = useAppSelector(selectIsLoading);

  return (
    <>
      <Routes>
        {/* Pre-launch landing page - standalone without nav/footer */}
        <Route path="/" element={<PreLaunchLandingPage />} />

        {/* Original landing page with mode selection - accessible at /select-mode */}
        <Route path="/select-mode" element={<LandingPage />} />

        {/* Mode redirects */}
        <Route path="/explorer" element={<Navigate to="/explorer/home" replace />} />
        <Route path="/adult" element={<Navigate to="/home" replace />} />
        <Route path="/admin" element={<Navigate to="/admin/home" replace />} />

        {/* App pages - with nav/footer */}
        <Route path="/*" element={<AppWrapper />} />
      </Routes>
      <GlobalLoadingOverlay isLoading={isLoading} />
    </>
  );
}

export default App;
